import { useMemo, useState } from "react";
import { ScheduleItem } from "./scheduleItemCard";
import { ScheduleItemSkeleton } from "./scheduleItemSkeleton";
import { EmptyState } from "./emptyState";
import { Link } from "@tanstack/react-router";
import { Button } from "../../@/components/ui/button";
import { BookOpenIcon, ArrowUpRight } from "lucide-react";
import { fromZonedTime } from "date-fns-tz";

const SOURCE_TIMEZONE = "Africa/Nairobi";

const parseTimeTodayInSourceTimezone = (hhmmss) => {
  const raw = String(hhmmss || "").trim();
  if (!raw) return null;
  const parts = raw.split(":");
  const hh = Number(parts[0]);
  const mm = Number(parts[1]);
  const ss = Number(parts[2] ?? 0);
  if (![hh, mm, ss].every((n) => Number.isFinite(n))) return null;
  const wallClock = new Date(1970, 0, 1, hh, mm, ss, 0);
  if (Number.isNaN(wallClock.getTime())) return null;
  return fromZonedTime(wallClock, SOURCE_TIMEZONE);
};

const getDayBucket = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime()))
    return "afterwards";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const dayAfter = new Date(today);
  dayAfter.setDate(today.getDate() + 2);
  if (date >= today && date < tomorrow) return "today";
  if (date >= tomorrow && date < dayAfter) return "tomorrow";
  return "afterwards";
};

const FILTERS = ["today", "tomorrow", "afterwards"];

const UpcomingClassesSection = ({
  isLoading,
  upcomingClasses,
  add,
  format,
}) => {
  const [filter, setFilter] = useState("today");
  const classes = Array.isArray(upcomingClasses) ? upcomingClasses : [];

  const filtered = useMemo(() => {
    return classes.filter((item) => {
      let start = null;
      if (
        item.startDateTime instanceof Date &&
        !Number.isNaN(item.startDateTime.getTime())
      ) {
        start = item.startDateTime;
      } else if (item.session_date) {
        start = new Date(item.session_date);
      }
      return getDayBucket(start) === filter;
    });
  }, [classes, filter]);

  const displayItems = filtered.slice(0, 3);

  return (
    <div className="max-md:mt-3 border border-(--neutral-300) rounded-lg">
      <div className="p-4">
        <h3 className="mb-1">Upcoming Classes</h3>
        <p className="text-(--neutral-800)">Click to join live session</p>
      </div>
      <div className="grid grid-cols-3 justify-start gap-2 items-start p-2 relative z-0">
        {FILTERS.map((f) => (
          <Button
            key={String(f)}
            size=""
            variant={String(filter) === String(f) ? "default" : "outline"}
            className={
              (String(filter) === String(f)
                ? "border-(--color-primary) text-white "
                : "") +
              " uppercase relative z-10"
            }
            onClick={() => setFilter(String(f))}
            type="button"
          >
            {String(f).charAt(0).toUpperCase() + String(f).slice(1)}
          </Button>
        ))}
      </div>
      <div className="flex flex-col gap-[8px]">
        <div>
          {isLoading ? (
            <div className="p-4">
              <ScheduleItemSkeleton />
              <ScheduleItemSkeleton />
              <ScheduleItemSkeleton />
            </div>
          ) : displayItems.length > 0 ? (
            displayItems.map((item, index) => {
              const startTime =
                item.startDateTime ||
                parseTimeTodayInSourceTimezone(item.session_start_time) ||
                new Date(`1970-01-01T${item.session_start_time}`);
              const endTime = add(startTime, {
                minutes: item.session_duration_minutes || 60,
              });

              const now = new Date();
              const isLive = now >= startTime && now <= endTime;

              const href =
                item.join_url ||
                item.session_meeting_link ||
                item.meetingLink ||
                item.session_link ||
                item.link ||
                "";

              return (
                <ScheduleItem
                  key={item.id ?? index}
                  title={item.title || item.class_title}
                  startTime={format(startTime, "p")}
                  endTime={format(endTime, "p")}
                  location={item.class_location || item.location}
                  variant={index === 0 ? "primary" : "default"}
                  href={href || undefined}
                  isLive={isLive}
                />
              );
            })
          ) : (
            <div className="flex flex-col justify-center items-center my-[12px] h-[180px] bg-(--neutral-100)">
              <EmptyState
                icon={BookOpenIcon}
                description="No live sessions scheduled."
              />
            </div>
          )}
        </div>
        <div className="mt-1 mb-2">
          <Link to="/live-classes">
            <Button
              variant="link"
              className="text-(--color-primary) w-full font-semibold hover:bg-transparent hover:text-(--color-accent)"
            >
              View Class Calendar
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UpcomingClassesSection;
